import type { SessionTimingPolicyByDuration } from '@einhaus/project314-types';
import { buildRegimePinChanges } from './regimePinsPresentation';
import type { StrategyEditorSectionId } from './strategyEditorSections';
import type { TradingStrategyFormData, TradingStrategyLegFormData } from './tradingStrategyEditorSubmitPayloads';

export interface StrategyEditorChange {
    section: StrategyEditorSectionId;
    label: string;
}

/**
 * Live and staged rule versions, used to label attachment changes as `v1 → v3` rather than as
 * opaque version ids. `tradeLogicV2*VersionId` is the field that actually persists; the definition
 * id and version number are display metadata resolved by a follow-up query, so only the version id
 * participates in change detection.
 */
export interface StrategyEditorAttachmentVersions {
    liveEntryVersion: number | null;
    liveExitVersion: number | null;
    stagedEntryVersion: number | null;
    stagedExitVersion: number | null;
}

const SESSION_TIMING_POLICY_DURATIONS = ['1m', '3m', '5m', '15m', '30m', '60m', '120m', '180m', '240m', '1d', '1w', '1mo'] as const;

/** Order-independent multiset comparison, so re-ordering a selection is not reported as an edit. */
export const haveSameNumberMembers = (first: number[] | undefined, second: number[] | undefined): boolean => {
    const firstValues = first ?? [];
    const secondValues = second ?? [];

    if (firstValues.length !== secondValues.length) return false;

    const firstValueCounts = new Map<number, number>();

    for (const value of firstValues) {
        firstValueCounts.set(value, (firstValueCounts.get(value) ?? 0) + 1);
    }

    for (const value of secondValues) {
        const remaining = firstValueCounts.get(value);

        if (remaining === undefined) return false;

        if (remaining === 1) {
            firstValueCounts.delete(value);
        } else {
            firstValueCounts.set(value, remaining - 1);
        }
    }

    return firstValueCounts.size === 0;
};

const haveSameStringMembers = (first: string[], second: string[]): boolean => {
    if (first.length !== second.length) return false;

    const firstValues = new Set(first);

    return second.every((value) => firstValues.has(value));
};

/** Order-independent fingerprint so a re-keyed policy object does not read as an edit. */
const fingerprintSessionTimingPolicy = (policy: SessionTimingPolicyByDuration | null): string => {
    if (!policy) return '';

    return SESSION_TIMING_POLICY_DURATIONS.map((duration) => {
        const durationPolicy = policy[duration];

        if (!durationPolicy) return `${duration}:off`;

        return `${duration}:${durationPolicy.detectionCutoff}|${durationPolicy.executionPolicy}|${durationPolicy.preCloseOffsetSecs ?? '-'}`;
    }).join(';');
};

const describeVersionChange = (liveVersion: number | null, stagedVersion: number | null): string => {
    const liveLabel = liveVersion === null ? 'none' : `v${liveVersion}`;
    const stagedLabel = stagedVersion === null ? 'none' : `v${stagedVersion}`;

    return `${liveLabel} → ${stagedLabel}`;
};

export const countVisibleTradingStrategyLegs = (legs: TradingStrategyLegFormData[]): number =>
    legs.filter((leg) => leg.isDeleted !== true).length;

const describeLegChanges = (legs: TradingStrategyLegFormData[]): string | null => {
    const addedCount = legs.filter((leg) => leg.isNew === true && leg.isDeleted !== true).length;
    const removedCount = legs.filter((leg) => leg.id !== undefined && leg.isDeleted === true).length;
    const modifiedCount = legs.filter((leg) => leg.id !== undefined && leg.isModified === true && leg.isDeleted !== true).length;

    const parts: string[] = [];

    if (addedCount > 0) parts.push(`${addedCount} added`);
    if (modifiedCount > 0) parts.push(`${modifiedCount} edited`);
    if (removedCount > 0) parts.push(`${removedCount} removed`);

    return parts.length === 0 ? null : `Legs: ${parts.join(', ')}`;
};

/**
 * Diffs the hydrated baseline against the working form and returns one labeled entry per changed
 * field, tagged with the section that owns it. The save bar renders these verbatim, and the section
 * nav derives its unsaved-change dots from the same list, so both always agree.
 */
export const buildStrategyEditorFormChanges = (
    baselineFormData: TradingStrategyFormData | null,
    formData: TradingStrategyFormData,
    attachmentVersions: StrategyEditorAttachmentVersions
): StrategyEditorChange[] => {
    if (baselineFormData === null) return [];

    const changes: StrategyEditorChange[] = [];

    if (baselineFormData.name !== formData.name) {
        changes.push({ section: 'setup', label: 'Name' });
    }

    if (baselineFormData.description !== formData.description) {
        changes.push({ section: 'setup', label: 'Description' });
    }

    if (baselineFormData.active !== formData.active) {
        changes.push({ section: 'setup', label: formData.active ? 'Status → Active' : 'Status → Inactive' });
    }

    if (baselineFormData.duration !== formData.duration) {
        changes.push({ section: 'setup', label: `Duration → ${formData.duration}` });
    }

    if (baselineFormData.entityListId !== formData.entityListId) {
        changes.push({ section: 'setup', label: 'Entity list scope' });
    }

    if (!haveSameStringMembers(baselineFormData.allowedUnderlyingEntityTypes, formData.allowedUnderlyingEntityTypes)) {
        changes.push({ section: 'setup', label: 'Allowed entity types' });
    }

    if (baselineFormData.afterHoursRemoved !== formData.afterHoursRemoved) {
        changes.push({ section: 'setup', label: 'Remove after-hours data' });
    }

    // Both of these are edited in the Backtests section, not Setup — the dot has to match where the field is.
    if (baselineFormData.autoBacktest !== formData.autoBacktest) {
        changes.push({ section: 'backtests', label: 'Scheduled backtest maintenance' });
    }

    if (
        !haveSameNumberMembers(
            baselineFormData.autoBacktestOptionBuyingStrategiesOverride,
            formData.autoBacktestOptionBuyingStrategiesOverride
        )
    ) {
        changes.push({ section: 'backtests', label: 'Option buying strategies' });
    }

    if (
        fingerprintSessionTimingPolicy(baselineFormData.sessionTimingPolicy) !==
        fingerprintSessionTimingPolicy(formData.sessionTimingPolicy)
    ) {
        changes.push({ section: 'setup', label: 'Session timing policy' });
    }

    for (const pinChange of buildRegimePinChanges(baselineFormData.regimeModelVersionOverrides, formData.regimeModelVersionOverrides)) {
        changes.push({ section: 'setup', label: pinChange.label });
    }

    if (baselineFormData.tradeLogicV2EntryVersionId !== formData.tradeLogicV2EntryVersionId) {
        changes.push({
            section: 'entry',
            label: `Entry rules ${describeVersionChange(attachmentVersions.liveEntryVersion, attachmentVersions.stagedEntryVersion)}`
        });
    }

    if (baselineFormData.tradeLogicV2ExitVersionId !== formData.tradeLogicV2ExitVersionId) {
        changes.push({
            section: 'exit',
            label: `Exit rules ${describeVersionChange(attachmentVersions.liveExitVersion, attachmentVersions.stagedExitVersion)}`
        });
    }

    const legChangeLabel = describeLegChanges(formData.legs);

    if (legChangeLabel !== null) {
        changes.push({ section: 'position', label: legChangeLabel });
    }

    return changes;
};

export interface StrategyEditorScreenerChangeInput {
    isInitialized: boolean;
    persistedScreenerRuleId: number | null;
    screenerRuleId: number | null;
}

export const buildStrategyEditorScreenerChanges = ({
    isInitialized,
    persistedScreenerRuleId,
    screenerRuleId
}: StrategyEditorScreenerChangeInput): StrategyEditorChange[] => {
    if (!isInitialized) return [];

    const changes: StrategyEditorChange[] = [];

    if (persistedScreenerRuleId !== screenerRuleId) {
        changes.push({ section: 'entry', label: screenerRuleId === null ? 'Screener detached' : `Screener → #${screenerRuleId}` });
    }

    return changes;
};

export const buildStrategyEditorSectionChangeCounts = (
    changes: StrategyEditorChange[]
): Partial<Record<StrategyEditorSectionId, number>> => {
    const countsBySection: Partial<Record<StrategyEditorSectionId, number>> = {};

    for (const change of changes) {
        countsBySection[change.section] = (countsBySection[change.section] ?? 0) + 1;
    }

    return countsBySection;
};
